import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface CustomerGroup {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  color: string | null;
  created_at: string;
  updated_at: string;
}

interface GroupWithCount extends CustomerGroup {
  member_count: number;
}

/**
 * CRUD for customer_groups (organization-level).
 */
export function useCustomerGroups(orgId?: string) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["customer-groups", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customer_groups" as any)
        .select("*, customer_group_members(count)")
        .eq("organization_id", orgId!)
        .order("name");
      if (error) throw error;
      return (data ?? []).map((g: any) => ({
        ...g,
        member_count: g.customer_group_members?.[0]?.count ?? 0,
      })) as GroupWithCount[];
    },
    enabled: !!orgId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["customer-groups"] });

  const createGroup = useMutation({
    mutationFn: async (values: { name: string; description?: string; color?: string; can_book_without_payment?: boolean }) => {
      const { error } = await supabase.from("customer_groups" as any).insert({
        organization_id: orgId!,
        name: values.name.trim(),
        description: values.description?.trim() || null,
        color: values.color || null,
        can_book_without_payment: values.can_book_without_payment ?? false,
      });
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Grupp skapad"); },
    onError: (e: Error) => {
      if (e.message?.includes("duplicate")) toast.error("Gruppen finns redan");
      else toast.error("Kunde inte skapa grupp");
    },
  });

  const updateGroup = useMutation({
    mutationFn: async ({ id, ...values }: { id: string; name?: string; description?: string | null; color?: string | null; can_book_without_payment?: boolean }) => {
      const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (values.name !== undefined) payload.name = values.name.trim();
      if (values.description !== undefined) payload.description = values.description?.trim() || null;
      if (values.color !== undefined) payload.color = values.color || null;
      if (values.can_book_without_payment !== undefined) payload.can_book_without_payment = values.can_book_without_payment;
      const { error } = await supabase.from("customer_groups" as any).update(payload).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Grupp uppdaterad"); },
    onError: () => toast.error("Kunde inte uppdatera grupp"),
  });

  const deleteGroup = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("customer_groups" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Grupp borttagen"); },
    onError: () => toast.error("Kunde inte ta bort grupp"),
  });

  return {
    groups: query.data ?? [],
    isLoading: query.isLoading,
    createGroup,
    updateGroup,
    deleteGroup,
  };
}

/**
 * Manage group membership for a single customer.
 */
export function useCustomerGroupMembers(customerId: string | undefined, orgId: string | undefined) {
  const qc = useQueryClient();

  const { data: allGroups = [] } = useQuery({
    queryKey: ["customer-groups", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customer_groups" as any)
        .select("*")
        .eq("organization_id", orgId!)
        .order("name");
      if (error) throw error;
      return (data ?? []) as CustomerGroup[];
    },
    enabled: !!orgId,
  });

  const { data: memberRows = [], isLoading } = useQuery({
    queryKey: ["customer-group-members", customerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customer_group_members" as any)
        .select("id, customer_group_id, customer_groups(id, name, color)")
        .eq("customer_id", customerId!);
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        customer_group_id: string;
        customer_groups: { id: string; name: string; color: string | null } | null;
      }>;
    },
    enabled: !!customerId,
  });

  const assignedGroups = memberRows
    .filter((r) => r.customer_groups)
    .map((r) => ({
      memberId: r.id,
      id: r.customer_groups!.id,
      name: r.customer_groups!.name,
      color: r.customer_groups!.color,
    }));

  const assignedIds = new Set(assignedGroups.map((g) => g.id));
  const availableGroups = allGroups.filter((g) => !assignedIds.has(g.id));

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["customer-group-members", customerId] });
    qc.invalidateQueries({ queryKey: ["customer-groups"] });
  };

  const addToGroup = useMutation({
    mutationFn: async (groupId: string) => {
      const { error } = await supabase.from("customer_group_members" as any).insert({
        customer_group_id: groupId,
        customer_id: customerId!,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: () => toast.error("Kunde inte lägga till i grupp"),
  });

  const removeFromGroup = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase.from("customer_group_members" as any).delete().eq("id", memberId);
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: () => toast.error("Kunde inte ta bort från grupp"),
  });

  return {
    assignedGroups,
    availableGroups,
    allGroups,
    isLoading,
    addToGroup,
    removeFromGroup,
  };
}
